import { EmrArrowUp } from "@medstonetech/slate-icons";
import * as React from "react";
import {
  Box,
  Button,
  chakra,
  Divider,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalContent,
  ModalFooter,
  UseDisclosureReturn,
  VStack,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { formMessages, genericErrors } from "messages";
import { useToast, useYupValidationResolver } from "hooks";
import { extractApiErrorMessage } from "utils";
import {
  AuditCommentThread,
  AuditCommentSharedThread,
} from "./AuditCommentThread";
import {
  useAuditComments,
  useAuditCommentsShared,
} from "modules/charts-shared/api/queries/useAuditComments";
import {
  useCreateAuditComments,
  useCreateAuditCommentsShared,
} from "modules/charts-shared/api/mutations/useCreateAuditComments";
import { Loading, StaffAvatar, ToolbarHeader } from "shared";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAuditPermissions } from "contexts";
import { UseMutateFunction } from "react-query";
import {
  useUpdateAuditStatus,
  UseUpdateAuditStatusPayload,
} from "modules/audits/api/mutations/useUpdateAuditStatus";
import { useAuditStatus } from "modules/audits/api/queries/useAuditStatus";
import { AuditStatus } from "modules/audits/types";
import {
  getAuditStatusForChartType,
  mapChartTypeToAuditType,
} from "modules/audits/utils";
import { ChartType } from "types";
import { useGetChart } from "modules/charts-shared/api/queries/useGetChart";

import { useAuth0 } from "@auth0/auth0-react";
import {
  useUpdateAuditCommentsReview,
  useUpdateAuditCommentsSharedReview,
} from "modules/charts-shared/api";
import { AuditCommentsIndicator } from "modules/charts-shared/types/audit-comments";

type AuditCommentsModalProps = {
  title: React.ReactNode;
  encounterId: string;
  chartCode: string;
  sectionCode: string;
  subsectionCode?: string;
  hasSubsection?: boolean;
  commentsIndicator?: AuditCommentsIndicator;
  commentsIndicatorLoading: boolean;
  onCommentCreated?: () => void;
} & Pick<UseDisclosureReturn, "isOpen" | "onClose">;

type AuditCommentsSharedModalProps = {
  title: React.ReactNode;
  chartCode: string;
  encounterId: string;
  sectionCode: string;
  subsectionCode?: string;
  commentsIndicator?: AuditCommentsIndicator;
  commentsIndicatorLoading: boolean;
  hasSubsection?: boolean;
  onCommentCreated?: () => void;
} & Pick<UseDisclosureReturn, "isOpen" | "onClose">;

type AuditCommentsModalToolBarProps = {
  title: React.ReactNode;
  onClose: () => void;
  isCommenter: boolean;
  auditOpenForChartType: boolean;
  commentsIndicator?: AuditCommentsIndicator;
  updateReview?: UseMutateFunction<unknown, unknown, unknown>;
  updateReviewLoading?: boolean;
};

type AuditCommentsModalBodyProps = {
  isLoading?: boolean;
} & ModalBodyProps;

type AuditCommentsModalFooterProps = {
  isLoading?: boolean;
  chartType?: ChartType;
  isAuditor: boolean;
  isCommenter: boolean;
  auditOpenForChartType: boolean;
  createComment: UseMutateFunction<unknown, unknown, AuditCommentForm>;
  isCreateCommentLoading: boolean;
  onCommentCreated?: () => void;
  updateAuditStatus: UseMutateFunction<
    unknown,
    unknown,
    UseUpdateAuditStatusPayload
  >;
  isUpdateAuditStatusLoading: boolean;
};

type AuditCommentForm = {
  comment: string;
};

const AuditCommentsModalBody = React.forwardRef<
  HTMLDivElement,
  AuditCommentsModalBodyProps
>((props, ref) => {
  const { isLoading, children, ...modalBodyProps } = props;

  return (
    <ModalBody
      flex="1"
      overflow="auto"
      padding="0px 12px 10px 12px"
      bg="gray.200"
      ref={ref}
      maxHeight="60vh"
      {...modalBodyProps}
    >
      <VStack spacing="1.25rem" paddingBottom=".5rem">
        {children}
      </VStack>
      {isLoading && <Loading />}
    </ModalBody>
  );
});

AuditCommentsModalBody.displayName = "AuditCommentsModalBody";

function AuditCommentsModalFooter({
  isLoading,
  isAuditor,
  isCommenter,
  auditOpenForChartType,
  createComment,
  isCreateCommentLoading,
  onCommentCreated,
}: AuditCommentsModalFooterProps) {
  const { user } = useAuth0();

  const CHART_COMMENT_FORM_SCHEMA = Yup.object().shape({
    comment: Yup.string().required(),
  });

  const resolver = useYupValidationResolver(CHART_COMMENT_FORM_SCHEMA);
  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<AuditCommentForm>({
    resolver,
  });

  const watchComment = watch("comment");

  const toast = useToast();

  const onSubmit: SubmitHandler<AuditCommentForm> = async (values) => {
    try {
      await createComment(values);
      onCommentCreated?.();
      reset({ comment: "" });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  const { onChange: registerOnChange, ...restRegister } = register("comment");
  const onChange: InputProps["onChange"] = (e) => {
    registerOnChange(e);
  };
  const onKeyPress: InputProps["onKeyPress"] = (e) => {
    if (e.key === "Enter") {
      handleSubmit(onSubmit)(e);
    }
  };

  return (
    <ModalFooter height="90px" bg="gray.50" px="32px" borderBottomRadius="30px">
      {isLoading && (
        <Box width="100%">
          <Loading />
        </Box>
      )}

      {!isLoading && (isAuditor || (isCommenter && auditOpenForChartType)) && (
        <Box
          width="100%"
          position="relative"
          display="flex"
          alignItems="center"
        >
          <VStack width="100%">
            <Box width="100%">
              <InputGroup display="flex" alignItems="center">
                <StaffAvatar
                  width="32px"
                  height="32px"
                  position="relative"
                  size="xs"
                  userName={user?.name || ""}
                  profileUrl={user?.pictureUrl || ""}
                  mr={5}
                />
                <Input
                  autoComplete="off"
                  placeholder="Add new comment here"
                  backgroundColor="white"
                  borderColor="white"
                  borderRadius="25px"
                  isDisabled={isSubmitting || isCreateCommentLoading}
                  onChange={onChange}
                  onKeyPress={onKeyPress}
                  _placeholder={{ color: "gray.500" }}
                  {...restRegister}
                />
                <InputRightElement width="unset" right="1rem">
                  <IconButton
                    aria-label="submit comment"
                    icon={
                      <Icon
                        as={EmrArrowUp}
                        color={Boolean(watchComment) ? "blue" : "gray.500"}
                        w="20px"
                        h="20px"
                      />
                    }
                    width="24px"
                    height="24px"
                    variant="ghost"
                    minWidth="unset"
                    bgColor="white"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isCreateCommentLoading}
                    isDisabled={isCreateCommentLoading}
                  />
                </InputRightElement>
              </InputGroup>
            </Box>
          </VStack>
        </Box>
      )}
    </ModalFooter>
  );
}

function AuditCommentsModalToolBar({
  title,
  onClose,
  isCommenter,
  auditOpenForChartType,
  commentsIndicator,
  updateReview,
  updateReviewLoading,
}: AuditCommentsModalToolBarProps) {
  const toast = useToast();

  const onReview = async () => {
    if (updateReview && !commentsIndicator?.hasReview) {
      try {
        await updateReview(null);
        toast({
          description: formMessages.updateSuccess("Audit subsection status"),
        });
      } catch (error) {
        toast({
          description:
            extractApiErrorMessage(error) || genericErrors.unknownError,
        });
      }

      onClose();
    }
  };

  return (
    <VStack gap="5px" py={4} bg="gray.200">
      <ToolbarHeader
        width="100%"
        px={6}
        mt={2}
        borderTopRadius="30px"
        display="flex"
        alignItems="center"
        titleText={
          <chakra.span
            fontSize="1.0625rem"
            fontWeight="600"
            color="black"
            alignSelf="center"
          >
            {title}
          </chakra.span>
        }
        rightButtons={[
          <Button key="doneBtn" onClick={onClose}>
            Hide
          </Button>,
        ]}
      />

      {isCommenter && auditOpenForChartType && (
        <Box
          margin="0px 2px"
          textAlign="center"
          wordBreak="break-word"
          fontSize="1.0625rem"
          fontWeight="600"
        >
          {commentsIndicator?.hasReview ? (
            <Button
              variant="outlineSquared"
              borderRadius="10px"
              w="240px"
              color="green"
              isDisabled={true}
            >
              Reviewed
            </Button>
          ) : (
            <Button
              variant="outlineSquared"
              borderRadius="10px"
              w="240px"
              onClick={onReview}
              isDisabled={commentsIndicator?.hasReview || updateReviewLoading}
            >
              Mark Reviewed
            </Button>
          )}
        </Box>
      )}
    </VStack>
  );
}

function AuditCommentsModal(props: AuditCommentsModalProps) {
  const {
    isOpen,
    onClose,
    title,
    encounterId,
    chartCode,
    sectionCode,
    subsectionCode,
    commentsIndicator,
    onCommentCreated,
  } = props;

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = React.useCallback(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: modalBodyRef.current.scrollHeight });
    }
  }, []);

  const requestParams = {
    encounterId,
    chartCode,
    sectionCode,
    subsectionCode,
  };

  const { data: chartData, isLoading: isChartLoading } = useGetChart({
    chartCode,
  });

  const { isAuditor, isCommenter } = useAuditPermissions();
  const { data: audit } = useAuditStatus(encounterId, {
    enabled: isCommenter && !!encounterId,
  });

  const currentChartAuditStatus = React.useMemo<AuditStatus | undefined>(
    () =>
      getAuditStatusForChartType(
        audit?.data,
        mapChartTypeToAuditType(chartData?.data?.type)
      ),
    [audit, chartData]
  );

  // Product rule: commenters may comment whenever the audit is OPEN (any status
  // except Completed). Chart field editing is the turn-gated action and is
  // controlled by `isCommenterReviewing` in ChartTracking, which requires InReview.
  const auditOpenForChartType =
    currentChartAuditStatus !== "Completed" &&
    currentChartAuditStatus !== undefined;

  const {
    data,
    isLoading: isAuditCommentsLoading,
    isFetching: isAuditsCommentsFetching,
  } = useAuditComments(requestParams);

  const { mutateAsync: createComment, isLoading: isCreateCommentLoading } =
    useCreateAuditComments(requestParams);

  const {
    mutateAsync: updateAuditStatus,
    isLoading: isUpdateAuditStatusLoading,
  } = useUpdateAuditStatus(encounterId);

  const {
    mutateAsync: updateReview,
    isLoading: isUpdateAuditCommentsReviewLoading,
  } = useUpdateAuditCommentsReview(requestParams);

  React.useEffect(() => {
    if (modalBodyRef.current) {
      scrollToBottom();
    }
  }, [data, scrollToBottom]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInRight">
      <ModalContent
        display="flex"
        flexDirection="column"
        width="28vw"
        maxWidth="92vh"
        bg="transparent"
        p={0}
        m={0}
        boxShadow="none"
        containerProps={{
          justifyContent: "flex-end",
          paddingRight: "2rem",
        }}
      >
        <Box boxShadow="md" mt={3}>
          <AuditCommentsModalToolBar
            title={title}
            onClose={onClose}
            isCommenter={isCommenter}
            auditOpenForChartType={auditOpenForChartType}
            updateReview={updateReview}
            commentsIndicator={commentsIndicator}
            updateReviewLoading={isUpdateAuditCommentsReviewLoading}
          />

          <Divider height="0px" />

          <AuditCommentsModalBody
            isLoading={
              isChartLoading ||
              isAuditCommentsLoading ||
              isAuditsCommentsFetching
            }
            ref={modalBodyRef}
          >
            {data?.data.map((comment) => (
              <AuditCommentThread
                key={comment.id}
                comment={comment}
                encounterId={encounterId}
                chartCode={chartCode}
                sectionCode={sectionCode}
                subsectionCode={subsectionCode}
                isAuditor={isAuditor}
                isCommenter={isCommenter}
                auditOpenForChartType={auditOpenForChartType}
              />
            ))}
          </AuditCommentsModalBody>

          <Divider height="0px" />

          <AuditCommentsModalFooter
            chartType={chartData?.data?.type}
            isAuditor={isAuditor}
            isCommenter={isCommenter}
            auditOpenForChartType={auditOpenForChartType}
            createComment={createComment}
            isCreateCommentLoading={isCreateCommentLoading}
            onCommentCreated={onCommentCreated}
            updateAuditStatus={updateAuditStatus}
            isUpdateAuditStatusLoading={isUpdateAuditStatusLoading}
          />
        </Box>
      </ModalContent>
    </Modal>
  );
}

function AuditCommentsSharedModal(props: AuditCommentsSharedModalProps) {
  const {
    isOpen,
    onClose,
    title,
    encounterId,
    chartCode,
    sectionCode,
    subsectionCode,
    commentsIndicator,
    onCommentCreated,
  } = props;

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  const scrollToBottom = React.useCallback(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: modalBodyRef.current.scrollHeight });
    }
  }, []);

  const { isAuditor, isCommenter } = useAuditPermissions();
  const { data: audit } = useAuditStatus(encounterId, {
    enabled: isCommenter && !!encounterId,
  });

  const { data: chartData, isLoading: isChartLoading } = useGetChart({
    chartCode,
  });

  const currentChartAuditStatus = React.useMemo<AuditStatus | undefined>(
    () =>
      getAuditStatusForChartType(
        audit?.data,
        mapChartTypeToAuditType(chartData?.data?.type)
      ),
    [audit, chartData]
  );

  // Product rule: commenters may comment whenever the audit is OPEN (any status
  // except Completed). Chart field editing is the turn-gated action and is
  // controlled by `isCommenterReviewing` in ChartTracking, which requires InReview.
  const auditOpenForChartType =
    currentChartAuditStatus !== "Completed" &&
    currentChartAuditStatus !== undefined;

  const requestParams = {
    encounterId,
    sectionCode,
    subsectionCode,
  };

  const { data, isLoading: isLoadingComments } =
    useAuditCommentsShared(requestParams);

  const { mutateAsync: createComment, isLoading: isCreateCommentLoading } =
    useCreateAuditCommentsShared(requestParams);

  const {
    mutateAsync: updateAuditStatus,
    isLoading: isUpdateAuditStatusLoading,
  } = useUpdateAuditStatus(encounterId);

  React.useEffect(() => {
    if (modalBodyRef.current) {
      scrollToBottom();
    }
  }, [data, scrollToBottom]);

  const { mutateAsync: updateReview, isLoading: isupdateReviewLoading } =
    useUpdateAuditCommentsSharedReview(requestParams);

  const isLoadingModal =
    isChartLoading ||
    isLoadingComments ||
    isCreateCommentLoading ||
    isUpdateAuditStatusLoading ||
    isupdateReviewLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInRight">
      <ModalContent
        display="flex"
        flexDirection="column"
        width="28vw"
        maxWidth="92vh"
        bg="transparent"
        p={0}
        m={0}
        boxShadow="none"
        containerProps={{
          justifyContent: "flex-end",
          paddingRight: "2rem",
        }}
      >
        <Box boxShadow="md">
          <AuditCommentsModalToolBar
            title={title}
            onClose={onClose}
            isCommenter={isCommenter}
            auditOpenForChartType={auditOpenForChartType}
            updateReview={updateReview}
            commentsIndicator={commentsIndicator}
            updateReviewLoading={isupdateReviewLoading}
          />
          <Divider height="0px" />

          {data && data.data.length > 0 && (
            <>
              <AuditCommentsModalBody
                isLoading={isLoadingModal}
                ref={modalBodyRef}
              >
                {data?.data.map((comment) => (
                  <AuditCommentSharedThread
                    key={comment.id}
                    comment={comment}
                    encounterId={encounterId}
                    sectionCode={sectionCode}
                    subsectionCode={subsectionCode}
                    isAuditor={isAuditor}
                    isCommenter={isCommenter}
                    auditOpenForChartType={auditOpenForChartType}
                  />
                ))}
              </AuditCommentsModalBody>
              <Divider height="0px" />
            </>
          )}

          {(isAuditor || (isCommenter && auditOpenForChartType)) && (
            <AuditCommentsModalFooter
              isLoading={isLoadingModal}
              chartType={chartData?.data?.type}
              isAuditor={isAuditor}
              isCommenter={isCommenter}
              auditOpenForChartType={auditOpenForChartType}
              createComment={createComment}
              isCreateCommentLoading={isCreateCommentLoading}
              onCommentCreated={onCommentCreated}
              updateAuditStatus={updateAuditStatus}
              isUpdateAuditStatusLoading={isUpdateAuditStatusLoading}
            />
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
}

export { AuditCommentsModal, AuditCommentsSharedModal };
